import { trackEvent } from '../util/analytics'
import { loadPlayer } from '../util/load_player'
import session from '../util/session'
import { on } from 'shared/javascripts/util/events'
import { postForm } from 'shared/javascripts/util/request'
import { getEmbedParent } from 'shared/javascripts/util/environment'
import type { AutoplayClearance, VideoData as PlayerVideoData } from 'player/javascripts/player'

type PlayerPromise = ReturnType<typeof loadPlayer>['playerPromise']

export type { AutoplayClearance }

export interface MainPlayerVideoData extends PlayerVideoData {
  id: number
  ad_server_options: Record<string, unknown>
  title: string
  /** As Rails stores it, `mm:ss`. Only ever displayed. */
  duration: string | null
  backlink_url?: string | null
  link_as_no_follow?: boolean
}

export interface AttachMainPlayerOptions {
  container: HTMLElement
  playerElement: HTMLElement
  video: MainPlayerVideoData
  showAds: boolean
  site?: string
  videoBanners?: HTMLElement
  substituteLogo?: HTMLElement
  preAdScreen?: HTMLElement
  skipVisitBookkeeping?: boolean
  startPosition?: number
  endPosition?: number
  autoplay?: boolean
  /** What an autoplaying player waits for before it starts, e.g. a modal covering the page. */
  autoplayClearance?: AutoplayClearance
  onPlaying?: () => void
  onPaused?: () => void
  onFinished?: () => void
  /** Every ad start, also one that is paused again right away; `onAdPlaying` follows only when it plays. */
  onAdStarted?: () => void
  onAdPlaying?: () => void
  onAdPaused?: () => void
}

export interface PlaybackState {
  playing: boolean
  position: number // in seconds
}

export interface MainPlayer {
  playerPromise: PlayerPromise
  play(): void
  pause(): void
  togglePlayback(): void
  pauseAndExitFullscreen(): void
  preventPlayback(): void
  allowPlayback(): void
  suspendForModal(): void
  resumeAfterModal(): void
  seek(seconds: number): void
  playbackState(): PlaybackState | undefined
  destroy(): Promise<void>
}

export function attachMainPlayer({
  container,
  playerElement,
  video,
  showAds,
  site,
  videoBanners,
  substituteLogo,
  preAdScreen,
  skipVisitBookkeeping = false,
  startPosition,
  endPosition,
  autoplay,
  autoplayClearance,
  onPlaying,
  onPaused,
  onFinished,
  onAdStarted,
  onAdPlaying,
  onAdPaused,
}: AttachMainPlayerOptions): MainPlayer {
  if (!skipVisitBookkeeping) {
    session.setVideoSeen(video.id)
  }

  const { playerPromise, destroyPlayer } = loadPlayer(container, {
    showAds,
    site,
    videoBanners,
    substituteLogo,
    preAdScreen,
    autoplay,
    autoplayClearance,
  })

  let resolvedPlayer: Awaited<PlayerPromise> | undefined
  let viewTracked = false
  let adTimestampNotified = false
  let adReceived = true
  let alreadyReloaded = false

  function trackView(): void {
    viewTracked = true
    void postForm('/video_stats_views', {
      education_level: session.educationLevel,
      number_of_semesters: session.numberOfSemesters,
      school_class_level: session.schoolGrade,
      study_course_id: session.studyCourseId,
      type_of_school: session.typeOfSchool,
      video_id: video.id,
    })
  }

  function onPlay(): void {
    playerElement.classList.add('-started')
    if (!viewTracked) {
      trackView()
    }
  }

  function handleFinished(): void {
    if (session.signedIn) {
      void postForm('/video_completions', { video_id: video.id })
    }
    onFinished?.()
  }

  function notifyAdTimestamp(event: Event & { time?: number }): void {
    const adPosition = video.ad_position == null ? null : Number(video.ad_position)
    if (
      adPosition != null &&
      !adTimestampNotified &&
      (event.type === 'app:player:adStarted' || (event.time && event.time > adPosition))
    ) {
      const adPlayed = adReceived
      trackEvent('player.adTimestampReached', { adPlayed, video }, { from: container })
      adTimestampNotified = true
    }
  }

  function onNoAdReceived(): void {
    adReceived = false
  }

  // Recover from a decode/network error once, resuming where playback stopped.
  function reload(): void {
    if (alreadyReloaded) {
      return
    }
    alreadyReloaded = true
    void playerPromise.then((player) => {
      player.loadVideo(video, video.ad_server_options, {
        seekTo: player.getCurrentTime(),
        skipAdsOnSeek: true,
      })
    })
  }

  function notifyEmbedParent(): void {
    const embedParent = getEmbedParent()
    if (!embedParent) {
      return
    }
    window.parent.postMessage(
      {
        backlinkUrl: video.backlink_url,
        linkAsNoFollow: video.link_as_no_follow,
        videoTitle: video.title,
        videoID: video.id,
        videoDuration: video.duration,
      },
      embedParent,
    )
  }

  void playerPromise.then((player) => {
    resolvedPlayer = player
    player.loadVideo(video, video.ad_server_options, {
      seekTo: startPosition,
      pauseAt: endPosition,
    })
    if (!skipVisitBookkeeping) {
      session.reduceRemindCounter()
    }
    // tracking: player.init
    trackEvent('player.init', { video }, { from: container })
    notifyEmbedParent()
  })

  const unsubscribes = [
    on(container, 'app:player:play', onPlay, { once: true }),
    on(container, 'playbackfinished.player', handleFinished),
    on(container, 'app:player:noAdReceived', onNoAdReceived),
    on(container, 'app:player:mediaError', reload),
    on(container, 'app:player:time app:player:adStarted', notifyAdTimestamp),
    on(container, 'app:player:play', () => onPlaying?.()),
    on(container, 'app:player:pause', () => onPaused?.()),
    // An ad that starts only to be paused again (see `Player#adPauseRequested`) does not take over playback.
    on(container, 'app:player:adStarted', () => {
      onAdStarted?.()
      if (!resolvedPlayer?.adPauseRequested) {
        onAdPlaying?.()
      }
    }),
    on(container, 'app:player:adResumed', () => onAdPlaying?.()),
    on(container, 'app:player:adPaused app:player:adFinished', () => onAdPaused?.()),
  ]

  return {
    playerPromise,

    play() {
      void playerPromise.then((player) => {
        void player.play()
      })
    },

    pause() {
      void playerPromise.then((player) => {
        player.pause()
      })
    },

    togglePlayback() {
      void playerPromise.then((player) => {
        if (player.isPlaying()) {
          player.pause()
        } else {
          void player.play()
        }
      })
    },

    pauseAndExitFullscreen() {
      void playerPromise.then((player) => {
        player.exitFullscreen()
        player.pause()
      })
    },

    preventPlayback() {
      void playerPromise.then((player) => {
        player.preventPlayback()
      })
    },

    allowPlayback() {
      void playerPromise.then((player) => {
        player.allowPlayback()
      })
    },

    suspendForModal() {
      void playerPromise.then((player) => {
        player.pausedByModal = (player.autoplay && !player.hasStarted()) || player.isPlaying()
        player.preventPlayback()
        player.pause()
      })
    },

    resumeAfterModal() {
      void playerPromise.then((player) => {
        player.allowPlayback()
        if (player.pausedByModal) {
          void player.playAndTryToUnmute()
        }
      })
    },

    seek(seconds) {
      void playerPromise.then((player) => {
        player.seek(seconds, { scroll: false })
      })
    },

    playbackState() {
      if (!resolvedPlayer) {
        return undefined
      }
      // videojs' currentTime() is undefined until metadata has loaded, despite what its types claim.
      const position = resolvedPlayer.getCurrentTime()
      return {
        playing: resolvedPlayer.isPlaying(),
        position: Number.isFinite(position) ? position : 0,
      }
    },

    async destroy() {
      for (const unsubscribe of unsubscribes) {
        unsubscribe()
      }
      await destroyPlayer().catch(() => {})
    },
  }
}
